import React, { useEffect, useRef } from 'react';

// Occasional meteor streaking across the void (~every 20s while idle).
const ShootingStars = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;

    const layout = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    layout();
    window.addEventListener('resize', layout);

    let star = null;
    let timer = null;

    const schedule = (min = 14000, max = 26000) => {
      timer = setTimeout(launch, min + Math.random() * (max - min));
    };

    const launch = () => {
      const fromLeft = Math.random() < 0.5;
      const ang = (18 + Math.random() * 22) * (Math.PI / 180);
      star = {
        x: fromLeft ? -60 : w + 60,
        y: h * (0.05 + Math.random() * 0.35),
        vx: (fromLeft ? 1 : -1) * (13 + Math.random() * 7) * Math.cos(ang),
        vy: (13 + Math.random() * 7) * Math.sin(ang),
        life: 0,
        max: 55 + Math.random() * 25,
        trail: [],
      };
    };

    let raf;
    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      if (star) {
        star.x += star.vx;
        star.y += star.vy;
        star.life += 1;
        star.trail.unshift({ x: star.x, y: star.y });
        if (star.trail.length > 22) star.trail.pop();

        const fade = Math.min(1, star.life / 8) * Math.max(0, 1 - star.life / star.max);
        ctx.globalCompositeOperation = 'lighter';
        for (let i = 0; i < star.trail.length - 1; i++) {
          const a = fade * (1 - i / star.trail.length) * 0.85;
          ctx.strokeStyle = `rgba(255, ${225 - i * 4}, ${190 - i * 5}, ${a})`;
          ctx.lineWidth = Math.max(0.4, 2.4 * (1 - i / star.trail.length));
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(star.trail[i].x, star.trail[i].y);
          ctx.lineTo(star.trail[i + 1].x, star.trail[i + 1].y);
          ctx.stroke();
        }
        const glow = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, 9);
        glow.addColorStop(0, `rgba(255, 250, 240, ${fade})`);
        glow.addColorStop(1, 'rgba(255, 250, 240, 0)');
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(star.x, star.y, 9, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';

        if (star.life > star.max || star.x < -120 || star.x > w + 120 || star.y > h + 120) {
          star = null;
          schedule();
        }
      }
      raf = requestAnimationFrame(tick);
    };
    schedule(6000, 14000);
    tick();

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      window.removeEventListener('resize', layout);
    };
  }, []);

  return <canvas ref={canvasRef} className="xp-galaxy" aria-hidden="true" data-testid="xp-shooting-stars" />;
};

export default ShootingStars;
